// Types pour le Module Soudures - Gestion globale des soudures de la tonnelle

/**
 * Types de soudures disponibles
 */
export enum TypeSoudure {
  MONTANT_POUTRE = 'montant_poutre',           // Soudure entre montant et poutre
  TRAVERSE_MONTANT = 'traverse_montant',       // Soudure entre traverse et montant
  POUTRE_POUTRE = 'poutre_poutre',            // Soudure entre poutres (coins)
  TRAVERSE_TRAVERSE = 'traverse_traverse',     // Soudure entre traverses (croisements)
  DOUBLE_POUTRE = 'double_poutre',            // Soudures de la double poutre
  ACCESSOIRE = 'accessoire'                   // Soudures d'accessoires
}

/**
 * Point 3D pour les positions de soudures
 */
export interface Point3D {
  x: number;
  y: number;
  z: number;
}

/**
 * Informations sur une soudure individuelle
 */
export interface Soudure {
  id: string;
  type: TypeSoudure;
  position: Point3D;
  taille: number;                              // Taille de la boule de soudure
  couleur: string;                             // Couleur de la soudure
  elementA: string;                            // ID du premier élément soudé
  elementB: string;                            // ID du deuxième élément soudé
  visible: boolean;                            // Visibilité de la soudure
}

/**
 * Paramètres globaux des soudures
 */
export interface ParametresSoudures {
  actif: boolean;                              // Activer/désactiver l'affichage des soudures
  taille: number;                              // Taille globale des soudures (0.01 à 0.1)
  couleur: string;                             // Couleur globale des soudures
  
  // Visibilité par type de soudure
  afficherMontantPoutre: boolean;
  afficherTraverseMontant: boolean;
  afficherPoutrePoutre: boolean;
  afficherTraverseTraverse: boolean;
  afficherDoublePoutre: boolean;
  afficherAccessoires: boolean;
  
  // Tailles spécifiques par type (multiplicateur de la taille globale)
  tailleMontantPoutre: number;
  tailleTraverseMontant: number;
  taillePoutrePoutre: number;
  tailleTraverseTraverse: number;
  tailleDoublePoutre: number;
  tailleAccessoires: number;
}

/**
 * Structure complète des soudures de la tonnelle
 */
export interface StructureSoudures {
  id: string;
  parametres: ParametresSoudures;
  soudures: Soudure[];
  statistiques: {
    total: number;
    parType: Record<TypeSoudure, number>;
  };
}

/**
 * Données nécessaires pour calculer les soudures
 */
export interface DonneesTonnelle {
  structureBase: {
    montants: Array<{
      id: string;
      position: Point3D;
      hauteur: number;
    }>;
    poutres: {
      poutreHaute: {
        points: Point3D[];
      };
      poutreBasse: {
        points: Point3D[];
      };
    };
  };
  traverses?: {
    rangees: Array<{
      id: string;
      hauteur: number;
      traverses: Array<{
        id: string;
        points: Point3D[];
      }>;
    }>;
  };
  doublePoutre?: {
    montants: Array<{
      id: string;
      position: Point3D;
      hauteur: number;
    }>;
    poutres: Array<{
      id: string;
      debut: Point3D;
      fin: Point3D;
    }>;
  };
}

/**
 * État du module soudures
 */
export interface SouduresState {
  parametres: ParametresSoudures;
  structure: StructureSoudures | null;
  isCalculating: boolean;
  error: string | null;
}

/**
 * Événements du module soudures
 */
export interface SouduresEvents {
  onParametresChange: (parametres: ParametresSoudures) => void;
  onSouduresCalculated: (structure: StructureSoudures) => void;
  onError: (error: string) => void;
}

/**
 * Configuration du module soudures
 */
export interface SouduresModuleConfig {
  couleurDefaut: string;
  tailleDefaut: number;
  toleranceDistance: number;                   // Distance minimale pour considérer deux éléments comme connectés
}

/**
 * Réponse de l'API pour les soudures
 */
export interface SouduresAPIResponse {
  success: boolean;
  data?: StructureSoudures;
  error?: string;
  timestamp: number;
}